import * as Tooltip from "@radix-ui/react-tooltip";
import { useEffect, useRef, useState } from "react";
import React from "react";
import { createPortal } from "react-dom";

import { cn } from "../../../lib/utils";

import { tooltipVariants } from "../../molecules/Tooltip";

type TooltipPosition = {
	top: number;
	left: number;
	width: number;
};

const ENTER_DELAY = 300;
const FADE_IN_DELAY = 50;
const EXIT_DELAY = 0;
const RESIZE_OBSERVER_DELAY = 150;
/** Overflow tolerance in px, for rendering differences of a pixel or two. */
export const CLAMP_PIXEL_THRESHOLD = 2;
const MAX_DEPTH = 4;

/**
 * Utility to check if a DOM element is clamped (text visually cut off).
 * Supports both multi-line clamp (-webkit-line-clamp) and single-line
 * truncation (text-overflow: ellipsis).
 */
const isClamped = (el: HTMLElement): boolean => {
	const computed = window.getComputedStyle(el);
	const lineClamp = parseInt(
		(computed as CSSStyleDeclaration & { webkitLineClamp?: string }).webkitLineClamp || "0",
		10
	);

	// For single-line truncation elements, check if there's significant overflow
	if (el.classList.contains("py-text-truncate")) {
		return el.scrollWidth > el.clientWidth + CLAMP_PIXEL_THRESHOLD;
	}

	// For line-clamp elements, check if there's significant overflow
	if (lineClamp > 0) {
		// Use a small threshold to account for couple pixel overflow rendering differences
		const hasOverflow = el.scrollHeight > el.clientHeight + CLAMP_PIXEL_THRESHOLD;

		return hasOverflow;
	}

	// If no line-clamp or truncation is set, the element is not clamped
	return false;
};

/**
 * Renders a global tooltip on hover for any text element with the 'line-clamp' class
 * if it is visually truncated (clamped).
 */
export const ClampedTextTooltipManager = () => {
	const [target, setTarget] = useState<HTMLElement | null>(null);
	const [isVisible, setIsVisible] = useState(false);
	const [text, setText] = useState("");
	const [position, setPosition] = useState<TooltipPosition>({ top: 0, left: 0, width: 0 });
	const observerRef = useRef<ResizeObserver | null>(null);
	const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	useEffect(() => {
		/**
		 * Handler for mouseover events – checks if target is clamped and tracks it.
		 */
		const handleMouseOver = (e: MouseEvent) => {
			const clampMatch = /^py-line-clamp-|^py-text-truncate$/;
			let el = e.target as HTMLElement | null;
			let depth = 0;

			while (el && depth++ < MAX_DEPTH) {
				if ([...el.classList].some((cls) => clampMatch.test(cls))) {
					if (isClamped(el)) {
						if (hoverTimeoutRef.current) {
							clearTimeout(hoverTimeoutRef.current);
						}

						hoverTimeoutRef.current = setTimeout(() => {
							// Check if element still exists in DOM and is still clamped
							if (!el || !document.contains(el) || !isClamped(el)) {
								return;
							}

							const rect = el.getBoundingClientRect();
							setTarget(el);
							setText(el.textContent || "");
							setPosition({
								top: rect.top,
								left: rect.left,
								width: rect.width,
							});
							setTimeout(() => setIsVisible(true), FADE_IN_DELAY);
						}, ENTER_DELAY);
					}
					break;
				}
				el = el.parentElement;
			}
		};

		/**
		 * Handler for mouseout – hide tooltip if user moves away.
		 */
		const handleMouseOut = (e: MouseEvent) => {
			// Clear the hover timeout if user moves out before it completes
			if (hoverTimeoutRef.current) {
				clearTimeout(hoverTimeoutRef.current);
				hoverTimeoutRef.current = null;
			}

			if (!target) return;
			const related = e.relatedTarget as Node;
			if (!target.contains(related)) {
				setIsVisible(false);
				// give a very small debounce to avoid flickering
				// too large and the tooltip might not render on the new element
				setTimeout(() => {
					setTarget(null);
					setText("");
				}, EXIT_DELAY);
			}
		};

		document.addEventListener("mouseover", handleMouseOver);
		document.addEventListener("mouseout", handleMouseOut);
		return () => {
			document.removeEventListener("mouseover", handleMouseOver);
			document.removeEventListener("mouseout", handleMouseOut);
			// Clean up timeout on unmount
			if (hoverTimeoutRef.current) {
				clearTimeout(hoverTimeoutRef.current);
			}
		};
	}, [target]);

	useEffect(() => {
		if (!target) return;

		const updatePosition = () => {
			const rect = target.getBoundingClientRect();
			setPosition({
				top: rect.top,
				left: rect.left,
				width: rect.width,
			});
		};

		// Observe target size/position changes and auto-hide if no longer clamped
		observerRef.current = new ResizeObserver(() => {
			if (!isClamped(target)) {
				setIsVisible(false);
				setTimeout(() => {
					setTarget(null);
					setText("");
				}, RESIZE_OBSERVER_DELAY);
			} else {
				updatePosition();
			}
		});

		observerRef.current.observe(target);
		// Capture phase so scrolling inside a container repositions the tooltip, not just the window.
		window.addEventListener("scroll", updatePosition, true);
		return () => {
			observerRef.current?.disconnect();
			observerRef.current = null;
			window.removeEventListener("scroll", updatePosition, true);
		};
	}, [target]);

	if (!target) return null;

	return createPortal(
		<Tooltip.Provider delayDuration={500}>
			<Tooltip.Root open={true}>
				{/* Invisible trigger positioned over the clamped element */}
				<Tooltip.Trigger asChild>
					<div
						style={{
							// Viewport-fixed to match getBoundingClientRect. Absolute positioning breaks inside a
							// modal, which locks scroll with `body { position: fixed; top: -scrollY }`.
							position: "fixed",
							top: position.top,
							left: position.left,
							width: position.width,
							height: target.offsetHeight,
							pointerEvents: "none", // allow mouse to interact with underlying element
						}}
						aria-hidden={!isVisible}
						aria-description={text}
					/>
				</Tooltip.Trigger>
				<Tooltip.Portal>
					<Tooltip.Content
						side="top"
						align="start"
						sideOffset={4}
						className={cn(
							"z-50 max-w-xs rounded break-words transition-all duration-150 ease-out",
							isVisible ? "translate-y-0 opacity-100" : "translate-y-1 opacity-0",
							tooltipVariants({
								size: "medium",
							})
						)}
						role="tooltip"
						aria-live="polite"
					>
						{text}
						<Tooltip.Arrow className="border-none fill-neutral-8" width={6} height={4} aria-hidden="true" />
					</Tooltip.Content>
				</Tooltip.Portal>
			</Tooltip.Root>
		</Tooltip.Provider>,
		document.body
	);
};
